// BLE bring-up screen — Super Admin only, mobile only.
//
// WHY THIS EXISTS. The BLE contract has four steps that all fail SILENTLY:
// a truncated write, a passive scan, an overlapping GATT call, a key mismatch.
// Every one of them presents as "nothing happened". Without the raw exchange in
// front of you, the only way to tell them apart is a laptop, a USB cable and
// nRF Connect — which is not available standing next to a switch on a wall.
//
// So this shows what was scanned, what went out, and what came back, verbatim.
// It is the app-side equivalent of §11's nRF Connect walkthrough, and it is the
// first thing to open when a device does not respond.
//
// Deliberately raw: no friendly errors, no retries, no interpretation. During
// bring-up, "AUTH FAIL" is more useful than "could not connect".

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, List, ListItemButton, ListItemText, Divider,
  CircularProgress, Chip, useTheme,
} from '@mui/material';
import { CenterTitleBar } from '../components/MobileAppBar';
import { copyText } from '../../../core/utils/clipboard';
import { bleSupported, scanForDevices, stopScan } from '../../../core/ble/bleClient';
import { openSession } from '../../../core/ble/bleSession';
import {
  CMD_PING, CMD_STATUS, CMD_INFO, CMD_WHOAMI,
} from '../../../core/ble/bleConfig';
import { deviceKey, toHex, selfTest, DEFAULT_KEY_VERSION } from '../../../core/ble/bleAuth';
import { parseStatus, channelsFor } from '../../../core/devices/statusFormat';

// Commands worth having as one tap during bring-up. PING is first because it is
// the only one allowed BEFORE auth — if PING answers and AUTH does not, the
// radio is fine and the problem is the key.
const PROBES = [CMD_PING, CMD_STATUS, CMD_INFO, CMD_WHOAMI, '1-1', '1-0', 'ALLOFF'];

const MONO = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 11, lineHeight: 1.45 };

export default function BleTestScreen() {
  const theme = useTheme();
  const c = theme.sli;

  const [devices, setDevices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [log, setLog] = useState([]);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const aliveRef = useRef(true);
  const seqRef = useRef(0);

  const add = useCallback((dir, text) => {
    seqRef.current += 1;
    const stamp = new Date().toTimeString().slice(0, 8);
    setLog((prev) => [{ id: seqRef.current, dir, text, stamp }, ...prev].slice(0, 300));
  }, []);

  useEffect(() => () => { aliveRef.current = false; stopScan(); }, []);

  // The maths is checked here, on the phone, against the spec's own vectors.
  // If this says FAIL, nothing else on this screen is worth trying — and it
  // rules the app out in one glance when a device rejects the key.
  const vectorsOk = selfTest();

  const scan = async () => {
    setDevices([]);
    setScanning(true);
    add('info', 'scanning (active, filtered on service 00FF)…');
    try {
      await scanForDevices((d) => {
        if (!aliveRef.current) return;
        setDevices((prev) => (prev.some((x) => x.deviceId === d.deviceId) ? prev : [...prev, d]));
        add('info', `found ${d.name} rssi=${d.rssi}${d.service ? ` state=${d.service.state} tag=${d.service.ownerTag}` : ' (no service data)'}`);
      });
    } catch (e) {
      add('err', (e && e.message) || 'scan failed');
    } finally {
      if (aliveRef.current) setScanning(false);
    }
  };

  const connectTo = async (d) => {
    if (!d.uniqueNumber) {
      add('err', `${d.name}: could not parse a unique number from the name — the auth key derives from it, so this device cannot be authenticated`);
      return;
    }
    setBusy(true);
    await stopScan();
    add('info', `device_key(${d.uniqueNumber}) = ${toHex(deviceKey(d.uniqueNumber, DEFAULT_KEY_VERSION))}`);
    const opened = await openSession(d.deviceId, d.uniqueNumber, {
      // ⚠️ THE TRACE IS THE ONLY LOGGER HERE, and it has to be. bleSession calls
      // trace('rx', …) for EVERY notification and THEN hands the post-handshake
      // ones to onStatus as well, so logging in both printed each status twice —
      // once annotated, once plain — which reads as the device having sent it
      // twice. Annotate in the trace; leave onStatus for state, not output.
      onTrace: (dir, text) => {
        if (dir !== 'rx') { add(dir, text); return; }
        const s = parseStatus(text);
        add('rx', s
          ? `${text}   → ${s.values.length} ch, source=${s.source || '—'}`
          : text);
      },
      onDisconnect: () => { setSession(null); add('info', 'disconnected'); },
    });
    if (!aliveRef.current) return;
    if (opened.ok) {
      setSession(opened.session);
      add('info', `AUTHENTICATED — device is ${opened.session.assigned ? 'ASSIGNED' : 'unassigned'}`);
    } else {
      add('err', `${opened.code}: ${opened.error}`);
    }
    setBusy(false);
  };

  const probe = async (cmd) => {
    if (!session) return;
    try {
      await session.send(cmd);
    } catch (e) {
      add('err', (e && e.message) || 'write failed');
    }
  };

  const colourFor = (dir) => (
    dir === 'tx' ? '#4FC3F7' : dir === 'rx' ? '#81C784' : dir === 'err' ? '#EF5350' : `${c.text}99`
  );

  if (!bleSupported()) {
    return (
      <Box sx={{ height: '100%', bgcolor: c.back }}>
        <CenterTitleBar title="BLE Test" showBack />
        <Typography sx={{ color: c.text, p: 3, textAlign: 'center' }}>
          Bluetooth only exists in the installed app, not the desktop browser.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar title="BLE Test" showBack />

      <Box sx={{ px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={vectorsOk ? 'auth vectors OK' : 'AUTH VECTORS FAIL'}
          color={vectorsOk ? 'success' : 'error'}
          sx={{ height: 20, fontSize: 11 }}
        />
        <Chip
          size="small"
          label={session ? `linked ${session.uniqueNumber}` : 'not linked'}
          color={session ? 'success' : 'default'}
          variant={session ? 'filled' : 'outlined'}
          sx={{ height: 20, fontSize: 11, color: session ? undefined : c.text }}
        />
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={scan} disabled={scanning || busy} sx={{ color: c.text, fontSize: 11 }}>
          {scanning ? 'Scanning…' : 'Scan'}
        </Button>
        {session && (
          <Button
            size="small"
            onClick={async () => { await session.close(); setSession(null); }}
            sx={{ color: c.text, fontSize: 11 }}
          >
            Disconnect
          </Button>
        )}
      </Box>

      {!session && (
        <Box sx={{ maxHeight: '32%', overflowY: 'auto', px: 1 }}>
          <List dense disablePadding>
            {devices.map((d) => (
              <ListItemButton key={d.deviceId} onClick={() => connectTo(d)} disabled={busy}>
                <ListItemText
                  primary={d.uniqueNumber || d.name}
                  secondary={[
                    d.service ? (d.service.assigned ? `assigned (${d.service.ownerTag})` : 'unassigned') : 'no service data',
                    `${d.rssi} dBm`,
                  ].join(' · ')}
                  primaryTypographyProps={{ sx: { color: c.text, fontWeight: 700, ...MONO } }}
                  secondaryTypographyProps={{ sx: { color: `${c.text}99`, fontSize: 11 } }}
                />
              </ListItemButton>
            ))}
          </List>
          {!scanning && devices.length === 0 && (
            <Typography sx={{ color: `${c.text}99`, fontSize: 12, p: 1.5, textAlign: 'center' }}>
              Nothing advertising service 00FF. If firmware BLE is compiled out,
              this is expected.
            </Typography>
          )}
        </Box>
      )}

      {session && (
        <Box sx={{ px: 1, py: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {PROBES.map((cmd) => (
            <Button
              key={cmd}
              size="small"
              variant="outlined"
              onClick={() => probe(cmd)}
              sx={{ minWidth: 0, fontSize: 11, py: 0.1, color: c.text, borderColor: `${c.text}55` }}
            >
              {cmd}
            </Button>
          ))}
        </Box>
      )}

      <Divider sx={{ borderColor: `${c.text}33` }} />

      <Box sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 12, flex: 1 }}>
          Exchange
        </Typography>
        <Button
          size="small"
          onClick={() => copyText(log.map((l) => `${l.stamp} ${l.dir} ${l.text}`).join('\n'))}
          disabled={log.length === 0}
          sx={{ color: c.text, fontSize: 11 }}
        >
          Copy
        </Button>
        <Button size="small" onClick={() => setLog([])} sx={{ color: c.text, fontSize: 11 }}>
          Clear
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 1 }}>
        {busy && <CircularProgress size={16} sx={{ color: c.text, m: 1 }} />}
        {log.map((l) => (
          <Box key={l.id} sx={{ ...MONO, color: colourFor(l.dir), py: '1px', wordBreak: 'break-all' }}>
            <Box component="span" sx={{ color: `${c.text}66`, mr: 0.5 }}>{l.stamp}</Box>
            <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
              {l.dir === 'tx' ? '→' : l.dir === 'rx' ? '←' : l.dir === 'err' ? '!' : '·'}
            </Box>
            {l.text}
          </Box>
        ))}
        {log.length === 0 && (
          <Typography sx={{ color: `${c.text}66`, fontSize: 12, p: 1.5, textAlign: 'center' }}>
            Scan, then tap a device. Every byte in and out appears here.
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.5, py: 0.5 }}>
        <Typography sx={{ color: `${c.text}66`, fontSize: 10 }}>
          {`4-touch = ${channelsFor(49)} channels · PING works before auth, so if PING answers and AUTH does not, the radio is fine and the key is wrong.`}
        </Typography>
      </Box>
    </Box>
  );
}
